import {
  Route,
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
} from "react-router-dom";
import { lazy, Suspense, ComponentType } from "react";
import Container from "./components/Container";
import Auth from "./pages/Auth/Auth";
import SSO from "./pages/Auth/SSO";
import HouseHoldLayout from "./pages/household/HouseHoldLayout";
import StockLayout from "./pages/stock/StockLayout";
import NoDataMessage from "./components/NoDataMessage";
import ErrorBoundary from "./components/ErrorBoundary";
import RoleProtectedRoute from "./components/RoleProtectedRoute";
import CacheBusterContainer from "./components/CacheBusterContainer";
import LoadingScreen from "./components/LoadingScreen";
import * as Sentry from "@sentry/react";

const createLazyComponent = (importFn: () => Promise<{ default: ComponentType<any> }>) => {
  const LazyComponent = lazy(importFn);
  return (
    <Suspense fallback={<LoadingScreen message="Loading..." />}>
      <LazyComponent />
    </Suspense>
  );
};

const createProtectedLazyComponent = (
  importFn: () => Promise<{ default: ComponentType<any> }>,
  requiredRole: "localAdmin" | "superAdmin"
) => {
  const LazyComponent = lazy(importFn);
  return (
    <RoleProtectedRoute requiredRole={requiredRole}>
      <Suspense fallback={<LoadingScreen message="Loading..." />}>
        <LazyComponent />
      </Suspense>
    </RoleProtectedRoute>
  );
};

function App() {
  return <RouterProvider router={router} />;
}

export default App;

const CONTAINER_ROUTES = [
  {
    route: "/household",
    element: createLazyComponent(() => import("./pages/household/HouseholdDictionary")),
  },
  {
    route: "/open-dictionary",
    element: createProtectedLazyComponent(
      () => import("./pages/open-dictionary/OpenDictionary"),
      "localAdmin"
    ),
  },
  {
    route: "/user",
    element: createProtectedLazyComponent(
      () => import("./pages/system-user/SystemUsers"),
      "localAdmin"
    ),
  },
  {
    route: "/stock",
    element: createProtectedLazyComponent(() => import("./pages/stock/StockList"), "localAdmin"),
  },
  {
    route: "/document-template",
    element: createProtectedLazyComponent(
      () => import("./pages/document-template/DocumentTemplate"),
      "localAdmin"
    ),
  },
  {
    route: "/admin-console",
    element: createProtectedLazyComponent(
      () => import("./pages/local-admin-console/LocalAdminConsole"),
      "localAdmin"
    ),
  },
  {
    route: "/global-admin-console",
    element: createProtectedLazyComponent(
      () => import("./pages/global-admin-console/GlobalAdminConsole"),
      "superAdmin"
    ),
  },
];

const HOUSEHOLD_ROUTES = [
  {
    route: "/household/:id",
    element: createLazyComponent(() => import("./pages/household/Household")),
  },
  {
    route: "/household/:id/unique-possibilities",
    element: createLazyComponent(() => import("./pages/household/UP/UniquePossibilities")),
  },
  {
    route: "/household/:id/npv",
    element: createLazyComponent(() => import("./pages/household/NPV/NetPresentValue")),
  },
  {
    route: "/household/:id/balance-sheet",
    element: createLazyComponent(() => import("./pages/household/balance-sheet/BalanceSheet")),
  },
  {
    route: "/household/:id/non-asset-balance-sheet",
    element: createLazyComponent(
      () => import("./pages/household/non-asset-balance-sheet/NonAssetBalanceSheet")
    ),
  },
  {
    route: "/household/:id/allocation-blueprint",
    element: createLazyComponent(
      () => import("./pages/household/allocation-blueprint/AllocationBlueprintWrapper")
    ),
  },
  {
    route: "/household/:id/stock-appreciation-rights",
    element: createLazyComponent(() => import("./pages/household/SAR/StockAppreciationRights")),
  },
  {
    route: "/household/:id/strategic-guide",
    element: createLazyComponent(() => import("./pages/household/strategic-guide/StrategicGuide")),
  },
  {
    route: "/household/:id/restricted-stock-awards",
    element: createLazyComponent(() => import("./pages/household/RSU/RestrictedStockAwards")),
  },
  {
    route: "/household/:id/performance-stock-awards",
    element: createLazyComponent(() => import("./pages/household/PSU/PerformanceStockAwards")),
  },
  {
    route: "/household/:id/gifting",
    element: createLazyComponent(() => import("./pages/household/gifting/Gifting")),
  },
  {
    route: "/household/:id/assets",
    element: createLazyComponent(() => import("./pages/household/assets/Assets")),
  },
  {
    route: "/household/:id/documents",
    element: createLazyComponent(() => import("./pages/household/documents/Documents")),
  },
  {
    route: "/household/:id/compensation-deferral-plan",
    element: createLazyComponent(() => import("./pages/household/plans/CompensationDeferralPlan")),
  },
  {
    route: "/household/:id/deferred-compensation-plan",
    element: createLazyComponent(() => import("./pages/household/plans/DeferredCompensationPlan")),
  },
  {
    route: "/household/:id/savings-restoration-plan",
    element: createLazyComponent(() => import("./pages/household/plans/SavingsRestorationPlan")),
  },
  {
    route: "/household/:id/deferred-long-term-incentive-plan",
    element: createLazyComponent(
      () => import("./pages/household/plans/DeferredLongTermIncentivePlan")
    ),
  },
  {
    route: "/household/:id/accounts-held-elsewhere",
    element: createLazyComponent(() => import("./pages/household/AHE/AccountsHeldElsewhere")),
  },
];

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

const router = sentryCreateBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path="/sso" element={<SSO />} errorElement={<ErrorBoundary />} />
      <Route path="/" element={<Auth />} errorElement={<ErrorBoundary />} />
      <Route element={<CacheBusterContainer />} errorElement={<ErrorBoundary />}>
        <Route element={<Container />} errorElement={<ErrorBoundary />}>
          <Route path="*" element={<NoDataMessage isPageNotFound />} />
          {CONTAINER_ROUTES.map(({ route, element }, i) => (
            <Route key={i} path={route} element={element} errorElement={<ErrorBoundary />} />
          ))}
        </Route>
        <Route element={<HouseHoldLayout />}>
          {HOUSEHOLD_ROUTES.map(({ route, element }, i) => (
            <Route key={i} path={route} element={element} errorElement={<ErrorBoundary />} />
          ))}
        </Route>
        <Route element={<StockLayout />}>
          <Route
            path="/stock/:id/browse"
            element={createLazyComponent(() => import("./pages/stock/StockDetail"))}
            errorElement={<ErrorBoundary />}
          />
        </Route>
      </Route>
    </>
  )
);
